// Availability management functions
import { collection, addDoc, getDocs, query, where, doc, updateDoc, deleteDoc, getDoc, setDoc } from 'firebase/firestore';
import { db } from './config';

/**
 * Get weekly schedule (day-of-week availability)
 */
export const getWeeklySchedule = async () => {
  try {
    const scheduleRef = doc(db, 'availability', 'weekly-schedule');
    const scheduleSnap = await getDoc(scheduleRef);
    
    if (scheduleSnap.exists()) {
      return scheduleSnap.data().schedule || {};
    }
    return {};
  } catch (error) {
    console.error('Error getting weekly schedule:', error);
    return {};
  }
};

/**
 * Set weekly schedule (day-of-week availability)
 */
export const setWeeklySchedule = async (schedule) => {
  try {
    const scheduleRef = doc(db, 'availability', 'weekly-schedule');
    await setDoc(scheduleRef, {
      schedule: schedule,
      updatedAt: new Date()
    }, { merge: true });
    return { success: true };
  } catch (error) {
    console.error('Error setting weekly schedule:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get day of week name (0 = Sunday, 6 = Saturday)
 */
const getDayOfWeek = (date) => {
  if (date instanceof Date) {
    return date.getDay();
  } else if (typeof date === 'string') {
    return new Date(date).getDay();
  } else {
    return new Date(date).getDay();
  }
};

/**
 * Get day name from day index
 */
const getDayName = (dayIndex) => {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[dayIndex];
};

/**
 * Generate time slots from a time range (24-hour format)
 */
const generateSlotsFromRange = (startTime, endTime, intervalMinutes = 30) => {
  const slots = [];
  
  const parseTime = (timeStr) => {
    // Handle both 24-hour format (HH:MM) and legacy 12-hour format (H:MM AM/PM)
    if (timeStr.includes(' ')) {
      // Legacy 12-hour format
      const [time, period] = timeStr.split(' ');
      const [hours, minutes] = time.split(':').map(Number);
      let hour24 = hours;
      if (period === 'PM' && hours !== 12) hour24 = hours + 12;
      if (period === 'AM' && hours === 12) hour24 = 0;
      return hour24 * 60 + minutes;
    } else {
      // 24-hour format (HH:MM)
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    }
  };
  
  const formatTime = (minutes) => {
    // Convert to 12-hour format for display (backward compatibility with existing slots)
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    let displayHours = hours;
    let period = 'AM';
    
    if (hours === 0) {
      displayHours = 12;
    } else if (hours === 12) {
      period = 'PM';
    } else if (hours > 12) {
      displayHours = hours - 12;
      period = 'PM';
    }
    
    return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
  };
  
  const startMinutes = parseTime(startTime);
  const endMinutes = parseTime(endTime);
  
  if (startMinutes >= endMinutes) return [];
  
  for (let minutes = startMinutes; minutes < endMinutes; minutes += intervalMinutes) {
    slots.push(formatTime(minutes));
  }
  
  return slots;
};

/**
 * Convert time ranges to slots
 */
const convertRangesToSlots = (ranges) => {
  if (!ranges || !Array.isArray(ranges)) return [];
  
  const allSlots = [];
  ranges.forEach(range => {
    if (range.start && range.end) {
      const slots = generateSlotsFromRange(range.start, range.end, range.interval || 30);
      allSlots.push(...slots);
    }
  });
  
  // Remove duplicates and sort
  const uniqueSlots = [...new Set(allSlots)];
  return uniqueSlots.sort((a, b) => {
    const parseTime = (timeStr) => {
      const [time, period] = timeStr.split(' ');
      const [hours, minutes] = time.split(':').map(Number);
      let hour24 = hours;
      if (period === 'PM' && hours !== 12) hour24 = hours + 12;
      if (period === 'AM' && hours === 12) hour24 = 0;
      return hour24 * 60 + minutes;
    };
    return parseTime(a) - parseTime(b);
  });
};

/**
 * Get configured time slots for a specific date (admin function - returns all configured slots, not filtered by bookings)
 * Priority: 1. Date-specific override, 2. Weekly schedule, 3. Defaults
 */
export const getConfiguredSlots = async (date) => {
  try {
    // Convert date to YYYY-MM-DD format
    let dateStr;
    if (date instanceof Date) {
      dateStr = date.toISOString().split('T')[0];
    } else if (typeof date === 'string') {
      dateStr = date.split('T')[0];
    } else {
      dateStr = new Date(date).toISOString().split('T')[0];
    }
    
    // First, check for date-specific override
    const availabilityRef = doc(db, 'availability', dateStr);
    const availabilitySnap = await getDoc(availabilityRef);
    
    if (availabilitySnap.exists()) {
      const availability = availabilitySnap.data();
      // If slots is explicitly set to empty array or null, that means "closed"
      if (availability.slots !== undefined) {
        return availability.slots || [];
      }
    }
    
    // Second, check weekly schedule for this day of week
    const dayOfWeek = getDayOfWeek(date);
    const dayName = getDayName(dayOfWeek);
    const weeklySchedule = await getWeeklySchedule();
    
    if (weeklySchedule[dayName]) {
      // Check for ranges first (new format)
      if (weeklySchedule[dayName].ranges && Array.isArray(weeklySchedule[dayName].ranges)) {
        return convertRangesToSlots(weeklySchedule[dayName].ranges);
      }
      // Fall back to slots (old format for backward compatibility)
      if (weeklySchedule[dayName].slots && Array.isArray(weeklySchedule[dayName].slots)) {
        return weeklySchedule[dayName].slots;
      }

    }
    
    // Third, use default availability
    return ["10:00 AM", "11:30 AM", "02:00 PM", "04:30 PM", "06:00 PM"];
  } catch (error) {
    console.error('Error getting configured slots:', error);
    // Return default slots on error
    return ["10:00 AM", "11:30 AM", "02:00 PM", "04:30 PM", "06:00 PM"];
  }
};

/**
 * Get all available time slots for a specific date
 * Priority: 1. Date-specific override, 2. Weekly schedule, 3. Defaults
 */
export const getAvailableSlots = async (date) => {
  try {
    // Convert date to YYYY-MM-DD format
    let dateStr;
    if (date instanceof Date) {
      dateStr = date.toISOString().split('T')[0];
    } else if (typeof date === 'string') {
      dateStr = date.split('T')[0];
    } else {
      dateStr = new Date(date).toISOString().split('T')[0];
    }
    
    // Get all bookings for this date
    const bookingsRef = collection(db, 'bookings');
    const q = query(
      bookingsRef,
      where('date', '==', dateStr),
      where('status', 'in', ['pending', 'confirmed'])
    );
    
    const snapshot = await getDocs(q);
    const bookedSlots = snapshot.docs.map(doc => doc.data().time);
    
    // Get configured slots (uses priority: override > weekly > defaults)
    const availableSlots = await getConfiguredSlots(date);
    
    // Filter out booked slots
    return availableSlots.filter(slot => !bookedSlots.includes(slot));
  } catch (error) {
    console.error('Error getting available slots:', error);
    // Return default slots on error
    return ["10:00 AM", "11:30 AM", "02:00 PM", "04:30 PM", "06:00 PM"];
  }
};

/**
 * Check if a specific date/time is available
 */
export const checkAvailability = async (date, time) => {
  try {
    // Convert date to YYYY-MM-DD format
    let dateStr;
    if (date instanceof Date) {
      dateStr = date.toISOString().split('T')[0];
    } else if (typeof date === 'string') {
      dateStr = date.split('T')[0];
    } else {
      dateStr = new Date(date).toISOString().split('T')[0];
    }
    
    // Check if there's already a booking
    const bookingsRef = collection(db, 'bookings');
    const q = query(
      bookingsRef,
      where('date', '==', dateStr),
      where('time', '==', time),
      where('status', 'in', ['pending', 'confirmed'])
    );
    
    const snapshot = await getDocs(q);
    return snapshot.empty; // Available if no bookings found
  } catch (error) {
    console.error('Error checking availability:', error);
    return false;
  }
};

/**
 * Set availability for a specific date (admin function - creates an override)
 */
export const setAvailability = async (date, slots) => {
  try {
    // Convert date to YYYY-MM-DD format
    let dateStr;
    if (date instanceof Date) {
      dateStr = date.toISOString().split('T')[0];
    } else if (typeof date === 'string') {
      dateStr = date.split('T')[0];
    } else {
      dateStr = new Date(date).toISOString().split('T')[0];
    }
    
    const availabilityRef = doc(db, 'availability', dateStr);
    
    await setDoc(availabilityRef, {
      date: dateStr,
      slots: slots,
      isOverride: true, // Mark as override
      updatedAt: new Date()
    }, { merge: true });
    
    return { success: true };
  } catch (error) {
    console.error('Error setting availability:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete availability override for a specific date (reverts to weekly schedule)
 */
export const deleteAvailabilityOverride = async (date) => {
  try {
    // Convert date to YYYY-MM-DD format
    let dateStr;
    if (date instanceof Date) {
      dateStr = date.toISOString().split('T')[0];
    } else if (typeof date === 'string') {
      dateStr = date.split('T')[0];
    } else {
      dateStr = new Date(date).toISOString().split('T')[0];
    }
    
    const availabilityRef = doc(db, 'availability', dateStr);
    await deleteDoc(availabilityRef);
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting availability override:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Check if a date has a custom override
 */
export const hasDateOverride = async (date) => {
  try {
    let dateStr;
    if (date instanceof Date) {
      dateStr = date.toISOString().split('T')[0];
    } else if (typeof date === 'string') {
      dateStr = date.split('T')[0];
    } else {
      dateStr = new Date(date).toISOString().split('T')[0];
    }
    
    const availabilityRef = doc(db, 'availability', dateStr);
    const availabilitySnap = await getDoc(availabilityRef);
    
    return availabilitySnap.exists() && availabilitySnap.data().isOverride === true;
  } catch (error) {
    console.error('Error checking date override:', error);
    return false;
  }
};

/**
 * Get all bookings (admin function)
 */
export const getAllBookings = async () => {
  try {
    const bookingsRef = collection(db, 'bookings');
    const snapshot = await getDocs(bookingsRef);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })).sort((a, b) => {
      // Sort by date, then time
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA - dateB;
      }
      return a.time.localeCompare(b.time);
    });
  } catch (error) {
    console.error('Error getting bookings:', error);
    return [];
  }
};

/**
 * Update booking status (admin function)
 */
export const updateBookingStatus = async (bookingId, status) => {
  try {
    const bookingRef = doc(db, 'bookings', bookingId);
    await updateDoc(bookingRef, {
      status: status,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating booking:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a booking (admin function)
 */
export const deleteBooking = async (bookingId) => {
  try {
    const bookingRef = doc(db, 'bookings', bookingId);
    await deleteDoc(bookingRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting booking:', error);
    return { success: false, error: error.message };
  }
};


